/**
 * Pure predicate for the invoice-detail header "EXCEEDS LIMIT" chip.
 *
 * The chip infers an amount-limit breach from the *shape* of the allowed
 * actions: the invoice sits in UNDER_REVIEW, APPROVE is not offered to the
 * current user, yet other header actions (reject/revise/override/…) are. On the
 * AP (non-FINS) path that combination only arises when the invoice amount
 * exceeds the current approver's limit and must escalate.
 *
 * That inference is categorically wrong for FINS (services) POs: there APPROVE
 * is gated on the OPS permission and the amount threshold is never consulted
 * (ADR 0023), so the chip would be false *and* contradict the "OPS Approval
 * Pending" badge rendered beside it. The FINS guard uses the shared
 * `isFinsSource` helper so it stays case-consistent with the label override
 * (getStatusLabel) — the two must never disagree on what counts as FINS.
 */
import { LIFECYCLE_STATUS, type LifecycleStatus } from "../types/common";
import { ACTION, type Action } from "../types/action";
import { isFinsSource } from "../constants/lifecycleStatus";

/**
 * Whether to render the "EXCEEDS LIMIT" chip.
 *
 * @param headerActions the allowed actions that surface as header buttons
 *   (i.e. `allowed_actions ∩ HEADER_ACTIONS`). APPROVE ∈ HEADER_ACTIONS, so its
 *   presence here is equivalent to APPROVE being allowed.
 */
export function showsExceedsLimitChip(params: {
  effectiveStatus?: LifecycleStatus | null;
  poSource?: string | null;
  headerActions?: readonly Action[];
}): boolean {
  const { effectiveStatus, poSource, headerActions = [] } = params;
  return (
    effectiveStatus === LIFECYCLE_STATUS.UNDER_REVIEW &&
    !isFinsSource(poSource) &&
    !headerActions.includes(ACTION.APPROVE) &&
    headerActions.length > 0
  );
}
